import useOffClick from '@flight-workspace/common-client/src/hooks/useOffClick';
import { AdBanner } from '@techminds/ad-banner';
import classNames from 'classnames/bind';
import { AreaCodeContext } from 'common/autocomplete/AreaCodeContext';
import { SelectAirportContext } from 'common/autocomplete/SelectAirportContext';
import { debounce } from 'common/utils';
import { Modal, ModalFuncContext, ModalPosition } from 'contexts/modalFunc';
import { ICity, TripType, useSearchParamsContext } from 'contexts/searchParams';
import { useCallback, useContext, useEffect, useState } from 'react';
import styles from './index.module.scss';
import PopularCities from './popularCities';
import RecentlySearched from './recentSearch';
import SearchResults from './searchResults';

const cx = classNames.bind(styles);
const adSlotInfo = {
  adUnitId: 'tm_travel_flight_searchbox_inline_image_banner',
  uct: 'KR',
};

interface IProps {
  forDeparture?: boolean;
  isSearchBoxFixed?: boolean;
  isEverywhere?: boolean;
}

export default function Autocomplete({
  forDeparture = false,
  isSearchBoxFixed = false,
  isEverywhere = false,
}: IProps) {
  const [value, setValue] = useState('');
  const setModal = useContext(ModalFuncContext);
  const areaCode = forDeparture ? 'dep' : 'arr';
  const [isAdLoaded, setIsLoaded] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const {
    searchParams: { tripType },
    setDepartureCity,
    setArrivalCity,
    setTripType,
  } = useSearchParamsContext();

  const closeModal = () => {
    setModal({ type: Modal.NULL, position: ModalPosition.MAIN });
  };

  const ref = useOffClick(closeModal);

  const onSelectAirport = (airport: ICity) => {
    if (tripType === TripType.EVERYWHERE) {
      setTripType(TripType.RT);
    }
    const setCity = forDeparture ? setDepartureCity : setArrivalCity;
    setCity(airport);
    closeModal();
  };

  const updateQuery = useCallback(
    debounce((value: string) => setDebouncedQuery(value), 300),
    [],
  );

  const handleAdLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    updateQuery(value.trim());
  }, [value, updateQuery]);

  return (
    <div
      className={cx('Autocomplete', {
        is_departure: forDeparture,
        is_arrival: !forDeparture,
        is_everywhere: isEverywhere,
      })}
      aria-modal="true"
      data-modalOnlyMobile="true"
      data-fixed={isSearchBoxFixed}
      ref={ref}
    >
      <div className={cx('header')}>
        <button
          type="button"
          className={cx('back')}
          aria-label="이전"
          data-event-nlog="click"
          data-event-area-code={`${forDeparture ? 'dep' : 'arr'}.back`}
          onClick={closeModal}
        />
        <div className={cx('query')}>
          <input
            className={cx('input')}
            placeholder="국가, 도시, 공항명 검색"
            type="text"
            value={value}
            data-event-nlog="click"
            data-event-area-code={`${forDeparture ? 'dep' : 'arr'}.window`}
            onChange={e => setValue(e.target.value)}
          />
          <button
            aria-label="삭제"
            className={cx('clear')}
            type="button"
            data-event-nlog="click"
            data-event-area-code={`${forDeparture ? 'dep' : 'arr'}.reset`}
            onClick={() => setValue('')}
          />
        </div>
      </div>
      <div className={cx('content')}>
        <AreaCodeContext.Provider value={areaCode}>
          <SelectAirportContext.Provider value={onSelectAirport}>
            {debouncedQuery ? (
              <SearchResults
                forDeparture={forDeparture}
                query={debouncedQuery}
                closeModal={closeModal}
              />
            ) : (
              <>
                {!forDeparture && (
                  <>
                    <RecentlySearched />
                    <div className={cx('SearchBox', isAdLoaded && 'loaded')}>
                      <AdBanner
                        adSlotInfo={adSlotInfo}
                        onAdLoaded={handleAdLoad}
                        className={cx('banner')}
                      />
                    </div>
                  </>
                )}
                <PopularCities
                  forDeparture={forDeparture}
                  isEverywhere={isEverywhere}
                />
              </>
            )}
          </SelectAirportContext.Provider>
        </AreaCodeContext.Provider>
      </div>
    </div>
  );
}
